import { Box, Typography, Stack, Avatar, Paper, Link, useMediaQuery, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { LogTabEventToGA } from '../../../shared/googleAnalytics/GoogleAnalytics';
import { howToCreateAgentLinkClickedText } from '../../../shared/googleAnalytics/GoogleAnalyticsConstants';

type Step = {
  number: number;
  title: string;
  description: string | React.ReactNode;
  links: { text: string; linkText?: string; href: string; download?: boolean }[];
};

const steps: Step[] = [
  {
    number: 1,
    title: 'Agents utilizing Wipro AI Platform (Hosted)',
    description:
      'Agents built with Wipro’s AI platform benefit from platform LLMs and its integrated guardrails and extensible agent frameworks, including Autogen and LangGraph. These agents have the flexibility to leverage the AI platform RAG and AI platform UI / custom UI for agents. Agents can be created and hosted in the marketplace with option to download agent code and agent try-out using "Play" button to try out the agent. For further inquiries, users may contact the agent owner directly.',
    links: [
      {
        text: 'How to create agent',
        href: 'termsAndPolicies/AgentCreationGuidelinePublisher.html',
      },
      {
        text: 'Azure Autogen sample code',
        href: 'https://lab45ftp.blob.core.windows.net/agents-hosting-guide/code/Azuresamplecode.zip',
        download: true,
      },
       {
        text: 'Azure Langgraph sample code',
        href: 'https://lab45ftp.blob.core.windows.net/agents-hosting-guide/code/new_sample_agent_langraph.zip',
        download: true,
      },
    ],
  },
  {
    number: 2,
    title: 'BYO Agents (Hosted)',
    description:
      'This option supports agents built in Python with Autogen or LangGraph, using cloud-based LLMs from Azure, Google, or AWS. Agents may use your LLMs locally during development, but API keys should not be included in published configuration files. Marketplace assigns the correct keys automatically according to the configuration. These agents have the flexibility to leverage custom UI for agents. Agents can be created and hosted in the marketplace with option to download agent code and agent try-out using "Play" button to try out the agent. For further inquiries, users may contact the agent owner directly.',
    links: [
      {
        text: 'How to create BYO agent',
        href: 'termsAndPolicies/ByoAgentGuide.html',
      },
      {
        text: 'Azure sample code',
        href: 'https://lab45ftp.blob.core.windows.net/agents-hosting-guide/code/summarise_meeting_requirements_agent.zip',
        download: true,
      },
      {
        text: 'ADK sample code',
        href: 'https://lab45ftp.blob.core.windows.net/agents-hosting-guide/code/google-adk-poc-agents.zip',
        download: true,
      },
      {
        text: 'GCP sample code',
        href: 'https://lab45ftp.blob.core.windows.net/agents-hosting-guide/code/GCP-Agent.zip',
        download: true,
      },
      {
        text: 'AWS sample code',
        href: 'https://lab45ftp.blob.core.windows.net/agents-hosting-guide/code/summarise_meeting_requirements_agent_AWS1.zip',
        download: true,
      },
    ],
  },
  {
    number: 3,
    title: 'Agent Card Option (Not Hosted)',
    description: (
      <>
        Users can submit essential agent metadata information via a template to generate an agent card within the Wipro
        Agent Marketplace. This enables users to review the agent description, access more detailed information about
        the agent, view videos, agent try-out using "Play" button to try out the agent or download the agent code from a
        blog, object storage, or a remote repository. For further inquiries, users may contact the agent owner directly.
        Please update agent details in the below template and send it across to{' '}
        <Link href="mailto:ai.support@wipro.com" color="primary">
          ai.support@wipro.com
        </Link>
        .
      </>
    ),
    links: [
      {
        text: 'Template for Agent Card',
        href: '/agentcardtemplate.xlsx',
      },
    ],
  },
  {
    number: 4,
    title: 'Application option (Not hosted)',
    description: (
      <>
        AI applications developed in Wipro can be listed in the Marketplace under the Application tab. Users fill out a
        template with basic metadata to create an application card, allowing users to view the description, access
        details, contact the owner, and try the app using the "Go" button. Please update application details in the
        below template and send it across to{' '}
        <Link href="mailto:ai.support@wipro.com" color="primary">
          ai.support@wipro.com
        </Link>
        .
      </>
    ),
    links: [
      {
        text: ' Template for Application Card',
        href: 'https://lab45ftp.blob.core.windows.net/agents-hosting-guide/artifacts/apptemplate1.xlsx',
        download: true,
      },
    ],
  },
];

const AgentPublishing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Box sx={{ backgroundColor: '#fff' }}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto', mt: 4, px: 2 }}>
        <Typography id="your-agent-publishing-journey" variant="h4" fontWeight={700} textAlign="center" sx={{ mb: 4 }}>
          Your Agent Publishing Journey
        </Typography>
        <Typography fontWeight={500} textAlign="left">
          Wipro Agent Marketplace offers multiple options to create, host, and publish your AI agents. All hosted agents
          will follow a PR (Pull Request) review process and will undergo a check for security scan of code or container
          scan reports without critical, major and high vulnerabilities. Detailed agent gating process will be followed
          before the agent is published in the marketplace. All hosted agents are paused if not used for 2 hours or
          more. When a user clicks "Start Agent," it takes 1-2 minutes to start, after which the "Play" button can be
          used to try out the agent. Each option is designed to support different development approach and hosting
          preferences. Below are the available options:
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 3, md: 10 },
          py: isMobile ? 4 : 8,
          maxWidth: '800px',
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            width: 70,
            height: 4,
            backgroundColor: '#1976d2',
            borderRadius: 2,
            mx: 'auto',
            mb: 2,
          }}
        />
        <Stack spacing={4}>
          {steps.map((step) => (
            <Stack direction="row" spacing={3} alignItems="center" justifyContent="center" key={step.number}>
              <Avatar
                sx={{
                  bgcolor: '#1976d2',
                  width: 60,
                  height: 60,
                  fontSize: 24,
                  fontWeight: 600,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {step.number}
              </Avatar>

              <Paper
                elevation={1}
                sx={{
                  borderRadius: 3,
                  p: 3,
                  flex: 1,
                  backgroundColor: '#f8f9fa',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom textAlign="left">
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom textAlign="left">
                  {step.description}
                </Typography>
                <Typography>
                  {step.links.map((link, index) => (
                    <Box key={index} display="flex" alignItems="center" sx={{ textAlign: 'left' }}>
                      {step.number === 3 && (
                        <Box sx={{ mt: 1 }}>
                          <Link
                            href={link.href}
                            download={link.href.split('/').pop()}
                            underline="hover"
                            color="primary"
                            sx={{ cursor: 'pointer', fontSize: 16 }}
                          >
                            {link.text}
                          </Link>
                        </Box>
                      )}

                      {step.number === 1 && (
                        link.download ? (
                          <Box sx={{ mt: 1 }}>
                            <Link
                              href={link.href}
                              download={link.href.split('/').pop()}
                              underline="hover"
                              color="primary"
                              sx={{ cursor: 'pointer', fontSize: 16 }}
                            >
                              {link.text}
                            </Link>
                          </Box>
                        ) : (
                          <Link
                            component={RouterLink}
                            to="/AgentCreationGuidelinePublisher"
                            underline="hover"
                            target="_blank"
                            color="primary"
                            onClick={() => LogTabEventToGA(howToCreateAgentLinkClickedText)}
                          >
                            {link.text}
                          </Link>
                        )
                      )}
                      {step.number === 2 && (
                        link.download ? (
                          <Box sx={{ mt: 1 }}>
                            <Link
                              href={link.href}
                              download={link.href.split('/').pop()}
                              underline="hover"
                              color="primary"
                              sx={{ cursor: 'pointer', fontSize: 16 }}
                            >
                              {link.text}
                            </Link>
                          </Box>
                        ) : (
                          <Link
                            component={RouterLink}
                            to="/ByoAgentGuide"
                            underline="hover"
                            target="_blank"
                            color="primary"
                            onClick={() => LogTabEventToGA(howToCreateAgentLinkClickedText)}
                            sx={{ cursor: 'pointer', fontSize: 16 }}
                          >
                            {link.text}
                          </Link>
                        )
                      )}
                      {step.number === 4 && (
                        <Box sx={{ mt: 1 }}>
                          <Link
                            href={link.href}
                            download={link.href.split('/').pop()}
                            underline="hover"
                            color="primary"
                            sx={{ cursor: 'pointer', fontSize: 16 }}
                          >
                            {link.text}
                          </Link>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Typography>
              </Paper>
            </Stack>
          ))}
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 8, mb: 2 }}>
            For any queries related to agent development, PR approval, security scan or general support, please contact{' '}
            <Link href="mailto:ai.support@wipro.com" color="primary">
              ai.support@wipro.com
            </Link>
            .
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default AgentPublishing;
